import { useState } from "react";
import { cn } from "@/lib/utils";
import type { InboxComment } from "@/hooks/use-inbox-comments";
import { Button } from "@/components/ui/button";
import {
  Heart,
  EyeOff,
  Trash2,
  Reply,
  MessageSquareReply,
  Image as ImageIcon,
  X,
  Send,
  Pin,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { ApiClientError } from "@/lib/api-client";
import { useBrandContextOptional } from "@/contexts/brand-context";
import { useMembers, type BrandRole } from "@/hooks/use-members";
import { DraftCard } from "@/components/inbox/draft-card";
import {
  useReplyToComment,
  usePrivateReplyToComment,
  useHideComment,
  useLikeComment,
  useDeleteComment,
  useAssignComment,
  useSetCommentStatus,
  usePinComment,
  useCommentReplies,
} from "@/hooks/use-inbox-comment-actions";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { INBOX_WRITE_ROLES } from "@/lib/inbox-roles";

const ASSIGN_ROLES: ReadonlySet<BrandRole> = new Set([
  "OWNER",
  "ADMIN",
  "CREATOR",
  "REVIEWER",
]);

interface Props {
  comment: InboxComment;
  selected?: boolean;
  onSelect?: () => void;
}

/** Friendly text for the documented engagement-limit errors (like / pin). */
function engagementErrorMessage(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const code = err instanceof ApiClientError ? err.code : null;
  const text = err instanceof Error ? err.message : "";
  if (code === "PLATFORM_BETA_RESTRICTED" || text.includes("PLATFORM_BETA_RESTRICTED")) {
    return t("commentCard.likeBetaRestricted");
  }
  if (
    code === "instagram_likes_require_facebook_login" ||
    text.includes("instagram_likes_require_facebook_login")
  ) {
    return t("commentCard.likeNeedsFacebookLogin");
  }
  return text || t("commentCard.actionFailed");
}

function formatTime(
  iso: string,
  locale: string,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("relativeTime.justNow");
  if (min < 60) return t("relativeTime.minutes", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("relativeTime.hours", { count: hr });
  return formatDate(d, locale, { month: "short", day: "numeric" });
}

export function CommentCard({ comment, selected, onSelect }: Props) {
  const { t } = useTranslation("inbox");
  const locale = useLocale();
  const initial = (comment.authorName ?? comment.authorId).charAt(0).toUpperCase();
  const brandCtx = useBrandContextOptional();
  const role = (brandCtx?.currentUserRole as BrandRole | undefined) ?? "VIEWER";
  const canWrite = INBOX_WRITE_ROLES.has(role);
  const canAssign = ASSIGN_ROLES.has(role);

  const [replyDraft, setReplyDraft] = useState<string | null>(null);
  const [privateDraft, setPrivateDraft] = useState<string | null>(null);
  const [showReplies, setShowReplies] = useState(false);

  const replyMut = useReplyToComment(comment.brandId);
  const privateMut = usePrivateReplyToComment(comment.brandId);
  const hideMut = useHideComment(comment.brandId);
  const likeMut = useLikeComment(comment.brandId);
  const deleteMut = useDeleteComment(comment.brandId);
  const assignMut = useAssignComment(comment.brandId);
  const statusMut = useSetCommentStatus(comment.brandId);
  const pinMut = usePinComment(comment.brandId);
  const replies = useCommentReplies(comment.brandId, comment.id, showReplies);
  const replyCount = comment.replyCount ?? 0;
  const membersQuery = useMembers(canAssign ? comment.brandId : "");

  const isInstagram = comment.platform ? comment.platform === "INSTAGRAM" : true;
  // TikTok for Business lets us pin a top-level comment to the top of the post.
  const canPin = comment.platform === "TIKTOK" && !comment.parentCommentId;

  function stop(e: React.MouseEvent) {
    e.stopPropagation();
  }

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect}
      className={cn(
        "flex gap-3 border-b border-border px-4 py-3 transition-colors",
        selected ? "bg-accent" : onSelect ? "hover:bg-surface-2 cursor-pointer" : "",
      )}
    >
      {/* Post thumbnail */}
      <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-surface-2 flex items-center justify-center">
        {comment.post.thumbnailUrl ? (
          <img
            src={comment.post.thumbnailUrl}
            alt={comment.post.pieceTitle ?? "post"}
            className="w-full h-full object-cover"
          />
        ) : (
          <ImageIcon size={20} className="text-muted-foreground/50" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-6 h-6 rounded-full bg-brand-subtle text-brand-subtle-text flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
            {initial}
          </span>
          <span className="text-[13px] font-semibold truncate">
            {comment.authorName ?? comment.authorId}
          </span>
          <span className="text-[11px] text-muted-foreground">·</span>
          <span className="text-[11px] text-muted-foreground">
            {formatTime(comment.postedAt, locale, t)}
          </span>
          {canAssign ? (
            <select
              onClick={stop}
              className="ml-auto rounded-md border border-border bg-surface-1 px-1.5 py-0.5 text-[11px]"
              value={comment.status}
              onChange={(e) =>
                statusMut.mutate({
                  commentId: comment.id,
                  status: e.target.value as "OPEN" | "SNOOZED" | "DONE",
                })
              }
            >
              <option value="OPEN">{t("status.OPEN")}</option>
              <option value="SNOOZED">{t("status.SNOOZED")}</option>
              <option value="DONE">{t("status.DONE")}</option>
            </select>
          ) : (
            <span className="ml-auto text-[11px] rounded-md border border-border bg-surface-1 px-1.5 py-0.5 text-muted-foreground">
              {t(`status.${comment.status}`, { defaultValue: comment.status })}
            </span>
          )}
        </div>
        {comment.post.pieceTitle && (
          <div className="text-[11px] text-muted-foreground mb-1 truncate">
            {t("commentCard.on", { title: comment.post.pieceTitle })}
          </div>
        )}
        <p
          className={cn(
            "text-[13px] whitespace-pre-wrap break-words",
            comment.hidden && "text-muted-foreground italic line-through",
          )}
        >
          {comment.text ?? <span className="italic">{t("commentCard.noText")}</span>}
        </p>
        {canWrite && (
          <div className="mt-2 flex items-center gap-1 flex-wrap" onClick={stop}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setReplyDraft(replyDraft === null ? "" : null)}
            >
              <Reply size={13} />
              <span className="text-[12px]">{t("commentCard.reply")}</span>
            </Button>
            {isInstagram && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() =>
                  setPrivateDraft(privateDraft === null ? "" : null)
                }
                title={t("commentCard.privateReplyTitle")}
              >
                <MessageSquareReply size={13} />
                <span className="text-[12px]">{t("commentCard.privateReply")}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={likeMut.isPending}
              onClick={() =>
                likeMut.mutate(
                  { commentId: comment.id, liked: !comment.likedByUs },
                  { onError: (err) => toast.error(engagementErrorMessage(err, t)) },
                )
              }
              title={comment.likedByUs ? t("commentCard.unlikeTitle") : t("commentCard.likeTitle")}
            >
              <Heart
                size={13}
                className={cn(comment.likedByUs && "fill-current text-destructive")}
              />
            </Button>
            {canPin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                disabled={pinMut.isPending}
                onClick={() =>
                  pinMut.mutate(
                    { commentId: comment.id, pinned: !comment.pinnedByUs },
                    { onError: (err) => toast.error(engagementErrorMessage(err, t)) },
                  )
                }
                title={comment.pinnedByUs ? t("commentCard.unpinTitle") : t("commentCard.pinTitle")}
              >
                <Pin size={13} className={cn(comment.pinnedByUs && "fill-current")} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={hideMut.isPending}
              onClick={() =>
                hideMut.mutate({ commentId: comment.id, hide: !comment.hidden })
              }
              title={comment.hidden ? t("commentCard.unhideTitle") : t("commentCard.hideTitle")}
            >
              <EyeOff size={13} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={deleteMut.isPending}
              onClick={() => {
                if (window.confirm(t("commentCard.deleteConfirm"))) {
                  deleteMut.mutate(comment.id);
                }
              }}
              title={t("commentCard.deleteTitle")}
            >
              <Trash2 size={13} />
            </Button>
            {canAssign && (
              <select
                className="h-7 ml-1 rounded-md border border-border bg-surface-1 px-1.5 text-[11px]"
                value={comment.assignedUserId ?? ""}
                onChange={(e) =>
                  assignMut.mutate({
                    commentId: comment.id,
                    userId: e.target.value || null,
                  })
                }
              >
                <option value="">{t("assignee.unassigned")}</option>
                {(membersQuery.data ?? []).map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name ?? m.email}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {replyCount > 0 && (
          <div className="mt-1.5" onClick={stop}>
            <button
              type="button"
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() => setShowReplies((v) => !v)}
              aria-expanded={showReplies}
            >
              {showReplies ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {t("commentCard.replies", { count: replyCount })}
            </button>
            {showReplies && (
              <div className="mt-1.5 space-y-1.5 border-l border-border pl-3">
                {replies.isLoading ? (
                  <p className="text-[11px] text-muted-foreground">{t("commentCard.repliesLoading")}</p>
                ) : replies.isError ? (
                  <p className="text-[11px] text-destructive">{t("commentCard.repliesError")}</p>
                ) : (
                  (replies.data?.replies ?? []).map((r) => (
                    <div key={r.id} className="text-[12px]">
                      <span className="font-semibold">
                        {r.authorName ?? r.authorUsername ?? "—"}
                      </span>
                      {r.fromOwnAccount && (
                        <span className="ml-1.5 rounded bg-brand-subtle px-1 py-px text-[10px] text-brand-subtle-text">
                          {t("commentCard.youBadge")}
                        </span>
                      )}
                      <span className="ml-1.5 whitespace-pre-wrap break-words">{r.text}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {canWrite && comment.aiDraft && comment.aiDraft.status === "PENDING" && (
          <div className="mt-2" onClick={stop}>
            <DraftCard
              brandId={comment.brandId}
              draft={comment.aiDraft}
              onEdit={(text) => setReplyDraft(text)}
            />
          </div>
        )}

        {replyDraft !== null && (
          <InlineReplyForm
            label={t("commentCard.replyLabel")}
            value={replyDraft}
            onChange={setReplyDraft}
            onCancel={() => setReplyDraft(null)}
            pending={replyMut.isPending}
            onSubmit={(text) =>
              replyMut.mutate(
                { commentId: comment.id, text },
                { onSuccess: () => setReplyDraft(null) },
              )
            }
          />
        )}
        {privateDraft !== null && (
          <InlineReplyForm
            label={t("commentCard.privateDmLabel")}
            value={privateDraft}
            onChange={setPrivateDraft}
            onCancel={() => setPrivateDraft(null)}
            pending={privateMut.isPending}
            onSubmit={(text) =>
              privateMut.mutate(
                { commentId: comment.id, text },
                { onSuccess: () => setPrivateDraft(null) },
              )
            }
          />
        )}
      </div>
    </div>
  );
}

function InlineReplyForm({
  label,
  value,
  onChange,
  onCancel,
  onSubmit,
  pending,
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
  onCancel: () => void;
  onSubmit: (text: string) => void;
  pending: boolean;
}) {
  return (
    <div className="mt-2 flex items-end gap-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex-1">
        <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full resize-none rounded-md border border-input bg-background px-2 py-1.5 text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <Button
        size="sm"
        className="h-7"
        disabled={!value.trim() || pending}
        onClick={() => onSubmit(value.trim())}
      >
        <Send size={12} />
      </Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={onCancel}>
        <X size={12} />
      </Button>
    </div>
  );
}
